import * as React from 'react';

import { getLocalTimeZone, now } from '@internationalized/date';
import {
  Badge,
  Button,
  DataGridColumn,
  LocalizedDate,
  Menu,
  MenuItem,
} from '@rematter/ui-web';
import { LucideArchive, LucideFiles, LucideMoreVertical } from 'lucide-react';

import { useTranslations } from '../../../i18n/useTranslations';
import {
  CustomPriceListRecord,
  CustomPriceListStatus,
} from '../../../types/pricing';

const computeStatus = (row: CustomPriceListRecord): CustomPriceListStatus => {
  if (row.archivedAt) return 'Archived';
  if (!row.isPublished) return 'Draft';
  if (row.endDate && row.endDate.compare(now(getLocalTimeZone())) < 0)
    return 'Expired';
  return 'Active';
};

const STATUS_COLOR: Record<CustomPriceListStatus, string> = {
  Archived: 'failure',
  Draft: 'neutral',
  Expired: 'warning',
  Active: 'success',
};

type Props = {
  onRowClick?: (row: CustomPriceListRecord) => void;
  onArchive?: (row: CustomPriceListRecord) => void;
  onDuplicate?: (row: CustomPriceListRecord) => void;
};

export const useCustomPriceListColumns = ({
  onRowClick,
  onArchive,
  onDuplicate,
}: Props = {}) => {
  const t = useTranslations();

  const handleCellDoubleClick = React.useCallback(
    ({ row }: { row: CustomPriceListRecord }) => {
      onRowClick?.(row);
    },
    [onRowClick],
  );

  const columns: DataGridColumn<CustomPriceListRecord>[] = React.useMemo(
    () => [
      {
        key: 'name',
        header: t.pricing.customPriceLists.table.columns.name,
        sortable: true,
        width: 200,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center text-left">{row.name}</div>
        ),
      },
      {
        key: 'operationType',
        header: t.pricing.customPriceLists.table.columns.operationType,
        sortable: true,
        width: 150,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => {
          const colorMap: Record<string, string> = {
            Purchase: 'warning',
            Sale: 'success',
            Brokerage: 'primary',
          };
          return (
            <Badge
              color={
                (colorMap[row.operationType] ?? 'neutral') as
                  | 'warning'
                  | 'success'
                  | 'primary'
                  | 'neutral'
              }
              size="small"
            >
              {row.operationType}
            </Badge>
          );
        },
      },
      {
        key: 'status',
        header: t.pricing.customPriceLists.table.columns.status,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => {
          const status = computeStatus(row);
          return (
            <Badge
              color={
                STATUS_COLOR[status] as
                  | 'failure'
                  | 'neutral'
                  | 'warning'
                  | 'success'
              }
              size="small"
            >
              {
                t.pricing.customPriceLists.status[
                  status.toLowerCase() as keyof typeof t.pricing.customPriceLists.status
                ]
              }
            </Badge>
          );
        },
      },
      {
        key: 'pricingMode',
        header: t.pricing.customPriceLists.table.columns.pricingMode,
        width: 100,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) =>
          row.pricingMode === 'CRV' ? (
            <Badge color="primary" size="small">
              CRV
            </Badge>
          ) : (
            <span className="text-neutral/60">Standard</span>
          ),
      },
      {
        key: 'currency',
        header: t.pricing.customPriceLists.table.columns.currency,
        width: 80,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => <span>{row.currency}</span>,
      },
      {
        key: 'startDate',
        header: t.pricing.customPriceLists.table.columns.startDate,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            {row.startDate ? (
              <LocalizedDate date={row.startDate} dateStyle="medium" />
            ) : (
              '-'
            )}
          </div>
        ),
      },
      {
        key: 'endDate',
        header: t.pricing.customPriceLists.table.columns.endDate,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            {row.endDate ? (
              <LocalizedDate date={row.endDate} dateStyle="medium" />
            ) : (
              '-'
            )}
          </div>
        ),
      },
      {
        key: 'createdAt',
        header: t.pricing.customPriceLists.table.columns.created,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            <LocalizedDate date={row.timestamps.createdAt} />
          </div>
        ),
      },
      {
        key: 'updatedAt',
        header: t.pricing.customPriceLists.table.columns.updated,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            <LocalizedDate date={row.timestamps.updatedAt} />
          </div>
        ),
      },
      ...(onArchive || onDuplicate
        ? [
            {
              key: 'actions',
              header: '',
              width: 50,
              cell: ({ row }: { row: CustomPriceListRecord }) => {
                const status = computeStatus(row);
                const items: MenuItem[] = [];
                if (onDuplicate) {
                  items.push({
                    label: t.pricing.customPriceLists.detail.actions.duplicate,
                    onAction: () => onDuplicate(row),
                    icon: <LucideFiles size={16} />,
                  });
                }
                if (onArchive && status !== 'Archived') {
                  items.push({
                    label: t.pricing.customPriceLists.detail.actions.archive,
                    onAction: () => onArchive(row),
                    icon: <LucideArchive size={16} />,
                  });
                }
                if (items.length === 0) return null;
                return (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                  >
                    <Menu
                      trigger={
                        <Button
                          size="icon"
                          variant="ghost"
                          color="neutral"
                          icon={<LucideMoreVertical size={16} />}
                        />
                      }
                      items={items}
                    />
                  </div>
                );
              },
            } satisfies DataGridColumn<CustomPriceListRecord>,
          ]
        : []),
    ],
    [t, handleCellDoubleClick, onArchive, onDuplicate],
  );

  const columnVisibilityState = React.useMemo(
    () => Object.fromEntries(columns.map((col) => [col.key, true])),
    [columns],
  );

  return { columns, columnVisibilityState };
};

export { computeStatus };
